import React, { useState, useEffect, useRef } from 'react';
import { X, Plus, Trash2, CheckCircle, Clock, AlertCircle, User, Search, Edit2 } from 'lucide-react';
import { collection, addDoc, doc, setDoc, getDocs, updateDoc, arrayUnion, query, where, getDoc, writeBatch } from "firebase/firestore";
import { NewCustomerModal } from './CustomerModals.js';
import useClickOutside from './hooks/useClickOutside.js';
import { Link } from 'react-router-dom'; // Added Link for InvoicePaymentsDetailModal
import { useConfig } from './hooks/useConfig.js';

const modalOverlayStyle = { position: 'fixed', top:0, left:0, right:0, bottom:0, backgroundColor: 'rgba(0,0,0,0.5)', display:'flex', justifyContent:'center', alignItems:'center', zIndex: 1000 };
const modalContentStyle = { backgroundColor: 'white', width: '500px', maxWidth:'95%', borderRadius: '8px', padding: '20px', display:'flex', flexDirection:'column', position: 'relative' };
const labelStyle = { display: 'block', fontSize: '11px', fontWeight: '600', color: '#6b7280', marginBottom: '4px', textTransform: 'uppercase' };
const inputStyle = { width: '100%', padding: '10px', borderRadius: '5px', border: '1px solid #d1d5db', fontSize: '14px', boxSizing: 'border-box', marginBottom: '15px' };
const btnPrimaryStyle = { backgroundColor: '#2563eb', color: 'white', border: 'none', padding: '12px 20px', borderRadius: '6px', cursor: 'pointer', fontWeight: 'bold' };
const dropdownStyle = { position: 'absolute', top: '100%', left: 0, width: '100%', maxHeight: '200px', overflowY: 'auto', backgroundColor: 'white', border: '1px solid #e5e7eb', borderRadius: '6px', boxShadow: '0 4px 6px rgba(0,0,0,0.1)', zIndex: 1100 };
const dropdownItemStyle = { padding: '10px', cursor: 'pointer', borderBottom: '1px solid #f3f4f6', fontSize: '13px' };

export const TransactionModal = ({ show, onClose, onSave, db, transactionData = null }) => {
    const [formData, setFormData] = useState({
        date: new Date().toISOString().split('T')[0],
        type: 'revenue',
        category: '',
        entityName: '', // New field for entity name
        firm: '', // Added firm field
        amount: '',
        remark: '',
        paymentMethod: 'Bank Transfer' // New field for payment method
    });
    const [categories, setCategories] = useState([]); // Reverted to original categories state
    const [firms, setFirms] = useState([]); // Added firms state
    const [showNewCategoryInput, setShowNewCategoryInput] = useState(false);
    const [newCategory, setNewCategory] = useState('');

    const handleRequestClose = () => {
        onClose();
    };

    const modalRef = useClickOutside(() => {
        if (show) handleRequestClose();
    });

    // Removed categorySearchRef and its useClickOutside usage

    useEffect(() => {
        if (show && !transactionData) {
            setFormData({
                date: new Date().toISOString().split('T')[0],
                type: 'revenue',
                category: '',
                entityName: '',
                firm: '',
                amount: '',
                remark: '',
                paymentMethod: 'Bank Transfer'
            });
            // Removed setCategorySearchTerm('')
            setNewCategory('');
            setShowNewCategoryInput(false);
        }
    }, [show, transactionData]);

    useEffect(() => {
        const fetchData = async () => {
            // Fetch Categories
            const catSnap = await getDocs(collection(db, "accounts_categories"));
            setCategories(catSnap.docs.map(doc => doc.data().name)); // Reverted to setting categories directly

            // Fetch Firms
            const firmSnap = await getDocs(collection(db, "firms"));
            setFirms(firmSnap.docs.map(doc => ({ id: doc.id, name: doc.data().name })));
        };
        if (show) fetchData();
    }, [db, show]);

    useEffect(() => {
        if (transactionData) {
            setFormData({
                date: transactionData.date,
                type: transactionData.type,
                category: transactionData.category,
                entityName: transactionData.entityName || '',
                firm: transactionData.firm || '', // Load existing firm
                amount: transactionData.amount,
                remark: transactionData.remark || '',
                paymentMethod: transactionData.paymentMethod || 'Bank Transfer' // Load existing payment method
            });
            // Removed setCategorySearchTerm(transactionData.category || '');
        }
    }, [transactionData]);

    if (!show) return null;

    // Removed handleCategorySearch and selectCategory functions

    const handleSave = async () => {
        // Reverted logic to use formData.category and newCategory directly
        if (!formData.category && !newCategory) return alert("Please select or enter a category");
        if (!formData.amount) return alert("Please enter amount");
        if (!formData.entityName) return alert("Please enter entity name (e.g., vendor, customer)");
        if (!formData.firm) return alert("Please select a firm"); // Validation for firm

        const finalCategory = showNewCategoryInput ? newCategory : formData.category;

        try {
            const payload = {
                ...formData,
                category: finalCategory,
                amount: parseFloat(formData.amount),
                createdAt: new Date().toISOString()
            };
            
            // Decide whether to add or update based on transactionData presence
            if (transactionData) {
                // This is an edit operation
                await updateDoc(doc(db, "accounts_transactions", transactionData.id), payload);

                // If this transaction is an invoice payment, update the invoice as well
                if (transactionData.sourceType === 'invoice' && transactionData.sourceId) {
                    const invoiceRef = doc(db, "accounts_invoices", transactionData.sourceId);
                    const invoiceSnap = await getDoc(invoiceRef);

                    if (invoiceSnap.exists()) {
                        const currentInvoiceData = invoiceSnap.data();
                        const oldAmount = transactionData.amount;
                        const newAmount = parseFloat(formData.amount);
                        const amountDifference = newAmount - oldAmount;

                        const updatedAmountPaid = (currentInvoiceData.amountPaid || 0) + amountDifference;
                        const updatedBalance = currentInvoiceData.totalAmount - updatedAmountPaid;
                        let newStatus = 'pending';
                        if (updatedBalance <= 0) newStatus = 'paid';
                        else if (updatedAmountPaid > 0) newStatus = 'partial';

                        // Update the specific payment entry within the invoice's payments array
                        const updatedPayments = currentInvoiceData.payments.map(p => 
                            p.transactionId === transactionData.id 
                                ? { ...p, amount: newAmount, date: formData.date, remark: formData.remark, method: formData.paymentMethod } // Also update method
                                : p
                        );

                        await updateDoc(invoiceRef, {
                            amountPaid: updatedAmountPaid,
                            balance: updatedBalance,
                            status: newStatus,
                            payments: updatedPayments,
                            firm: formData.firm // Keep firm in sync
                        });
                    } else {
                        console.warn("Corresponding invoice not found for transaction update:", transactionData.sourceId);
                    }
                }

            } else {
                // This is a new transaction operation
                await addDoc(collection(db, "accounts_transactions"), payload);
            }
            
            if (showNewCategoryInput) {
                if (!categories.includes(newCategory)) { // Use original categories state
                    await addDoc(collection(db, "accounts_categories"), { name: newCategory });
                }
            }
            
            onSave();
            onClose();
        } catch (e) {
            alert("Error saving transaction: " + e.message);
        }
    };

    return (
        <div style={modalOverlayStyle}>
            <div style={modalContentStyle} ref={modalRef}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                    <h3 style={{ margin: 0 }}>{transactionData ? 'Edit' : 'Add'} Transaction</h3>
                    <button onClick={handleRequestClose} style={{ background: 'none', border: 'none', cursor: 'pointer' }}><X /></button>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                    <div>
                        <label style={labelStyle}>Date</label>
                        <input type="date" value={formData.date} onChange={e => setFormData({...formData, date: e.target.value})} style={inputStyle} />
                    </div>
                    <div>
                        <label style={labelStyle}>Type</label>
                        <select value={formData.type} onChange={e => setFormData({...formData, type: e.target.value})} style={inputStyle}>
                            <option value="revenue">Revenue (Inflow)</option>
                            <option value="expense">Expense (Outflow)</option>
                        </select>
                    </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                    <div>
                        <label style={labelStyle}>Firm *</label>
                        <select 
                            value={formData.firm} 
                            onChange={e => setFormData({...formData, firm: e.target.value})} 
                            style={inputStyle}
                        >
                            <option value="">Select Firm</option>
                            {firms.map(f => <option key={f.id} value={f.name}>{f.name}</option>)}
                        </select>
                    </div>
                    <div>
                        <label style={labelStyle}>Category</label>
                        {!showNewCategoryInput ? (
                            <div style={{ display: 'flex', gap: '10px', marginBottom: '15px' }}>
                                <select 
                                    value={formData.category} 
                                    onChange={e => setFormData({...formData, category: e.target.value})} 
                                    style={{ ...inputStyle, marginBottom: 0, flex: 1 }}
                                >
                                    <option value="">Select Category</option>
                                    {categories.map(c => <option key={c} value={c}>{c}</option>)}
                                </select>
                                <button onClick={() => setShowNewCategoryInput(true)} style={{ padding: '0 10px', borderRadius: '5px', border: '1px solid #d1d5db', background: '#f3f4f6' }}><Plus size={18}/></button>
                            </div>
                        ) : (
                            <div style={{ display: 'flex', gap: '10px', marginBottom: '15px' }}>
                                <input 
                                    placeholder="New Category Name" 
                                    value={newCategory} 
                                    onChange={e => setNewCategory(e.target.value)} 
                                    style={{ ...inputStyle, marginBottom: 0, flex: 1 }}
                                />
                                <button onClick={() => setShowNewCategoryInput(false)} style={{ padding: '0 10px', borderRadius: '5px', border: '1px solid #d1d5db', background: '#f3f4f6' }}><X size={18}/></button>
                            </div>
                        )}
                    </div>
                </div>

                <div>
                    <label style={labelStyle}>{formData.type === 'revenue' ? 'Receivable from *' : 'Payable to *'}</label>
                    <input 
                        type="text"
                        value={formData.entityName} 
                        onChange={e => setFormData({...formData, entityName: e.target.value})} 
                        style={inputStyle} 
                        placeholder="Enter name..."
                        autoComplete="off"
                    />
                </div>

                <label style={labelStyle}>Amount</label>
                <input type="number" placeholder="0.00" value={formData.amount} onChange={e => setFormData({...formData, amount: e.target.value})} style={inputStyle} />
                
                <label style={labelStyle}>Payment Method</label>
                <select value={formData.paymentMethod} onChange={e => setFormData({...formData, paymentMethod: e.target.value})} style={inputStyle}>
                    <option>Bank Transfer</option>
                    <option>UPI / GPay</option>
                    <option>Cash</option>
                    <option>Cheque</option>
                </select>

                <label style={labelStyle}>Remark / Description</label>
                <textarea placeholder="Optional details..." value={formData.remark} onChange={e => setFormData({...formData, remark: e.target.value})} style={{ ...inputStyle, minHeight: '80px' }} />

                <button onClick={handleSave} style={btnPrimaryStyle}>Save Transaction</button>
            </div>
        </div>
    );
};

export const AccountsInvoiceModal = ({ show, onClose, onSave, db, invoiceData = null }) => {
    const { config } = useConfig();
    const [formData, setFormData] = useState({
        invoiceNo: '',
        customerID: '',
        customerName: '',
        salesPerson: '',
        firm: '', // Added firm field
        totalAmount: '',
        issueDate: new Date().toISOString().split('T')[0],
        dueDate: '',
        remark: ''
    });

    const handleRequestClose = () => {
        onClose();
    };

    // --- CUSTOMER SEARCH STATE ---
    const [allCustomers, setAllCustomers] = useState([]);
    const [firms, setFirms] = useState([]); // Added firms state
    const [filteredCustomers, setFilteredCustomers] = useState([]);
    const [showCustSuggestions, setShowCustSuggestions] = useState(false);
    const [customerSearchTerm, setCustomerSearchTerm] = useState('');
    const [showNewCustModal, setShowNewCustModal] = useState(false);
    
    const customerSearchRef = useClickOutside(() => setShowCustSuggestions(false));
    
    const modalRef = useClickOutside(() => {
        if (show && !showNewCustModal) handleRequestClose();
    });

    useEffect(() => {
        if (show && !invoiceData) {
            setFormData({
                invoiceNo: '',
                customerID: '',
                customerName: '',
                salesPerson: '',
                firm: '',
                totalAmount: '',
                issueDate: new Date().toISOString().split('T')[0],
                dueDate: '',
                remark: ''
            });
            setCustomerSearchTerm('');
        }
    }, [show, invoiceData]);

    useEffect(() => {
        const fetchData = async () => {
            const custSnap = await getDocs(collection(db, "customers"));
            setAllCustomers(custSnap.docs.map(doc => ({ id: doc.id, ...doc.data() })));

            const firmSnap = await getDocs(collection(db, "firms"));
            setFirms(firmSnap.docs.map(doc => ({ id: doc.id, name: doc.data().name })));
        };
        if (show) fetchData();
    }, [db, show]);

    useEffect(() => {
        if (invoiceData) {
            setFormData({
                invoiceNo: invoiceData.invoiceNo,
                customerID: invoiceData.customerID || '',
                customerName: invoiceData.customerName,
                salesPerson: invoiceData.salesPerson,
                firm: invoiceData.firm || '', // Load existing firm
                totalAmount: invoiceData.totalAmount,
                issueDate: invoiceData.issueDate,
                dueDate: invoiceData.dueDate,
                remark: invoiceData.remark || ''
            });
            setCustomerSearchTerm(invoiceData.customerName);
        }
    }, [invoiceData]);

    if (!show) return null;

    const handleCustomerSearch = (val) => {
        setCustomerSearchTerm(val);
        setFormData(prev => ({ ...prev, customerName: val })); // Keep name in sync for simplified cases
        if (val.length > 0) {
            const matches = allCustomers.filter(c => c.display_name?.toLowerCase().includes(val.toLowerCase()));
            setFilteredCustomers(matches);
            setShowCustSuggestions(true);
        } else {
            setShowCustSuggestions(false);
        }
    };

    const selectCustomer = (cust) => {
        setFormData(prev => ({ ...prev, customerID: cust.id, customerName: cust.display_name }));
        setCustomerSearchTerm(cust.display_name);
        setShowCustSuggestions(false);
    };

    const handleSave = async () => {
        if (!formData.invoiceNo || !formData.customerName || !formData.totalAmount || !formData.firm) {
            return alert("Please fill mandatory fields including Firm");
        }

        try {
            const payload = {
                ...formData,
                totalAmount: parseFloat(formData.totalAmount),
                amountPaid: invoiceData ? invoiceData.amountPaid : 0,
                balance: invoiceData ? (parseFloat(formData.totalAmount) - invoiceData.amountPaid) : parseFloat(formData.totalAmount),
                status: invoiceData ? invoiceData.status : 'pending',
                payments: invoiceData ? invoiceData.payments : [],
                createdAt: invoiceData ? invoiceData.createdAt : new Date().toISOString(),
                updatedAt: new Date().toISOString()
            };

            if (invoiceData) {
                await updateDoc(doc(db, "accounts_invoices", invoiceData.id), payload);

                // Update Firm in all linked transactions
                try {
                    const transQ = query(collection(db, "accounts_transactions"), where("sourceId", "==", invoiceData.id));
                    const transSnap = await getDocs(transQ);
                    if (!transSnap.empty) {
                        const batch = writeBatch(db);
                        transSnap.docs.forEach((tDoc) => {
                            batch.update(tDoc.ref, { firm: formData.firm });
                        });
                        await batch.commit();
                    }
                } catch (err) {
                    console.error("Error syncing firm to transactions:", err);
                }
            } else {
                await addDoc(collection(db, "accounts_invoices"), payload);
            }
            onSave();
            onClose();
        } catch (e) {
            alert("Error saving invoice");
        }
    };

    return (
        <>
            <div style={modalOverlayStyle}>
                <div style={{ ...modalContentStyle, width: '600px', overflow: 'visible' }} ref={modalRef}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                        <h3 style={{ margin: 0 }}>{invoiceData ? 'Edit' : 'New'} Invoice</h3>
                        <button onClick={handleRequestClose} style={{ background: 'none', border: 'none', cursor: 'pointer' }}><X /></button>
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                        <div>
                            <label style={labelStyle}>Firm *</label>
                            <select 
                                value={formData.firm} 
                                onChange={e => setFormData({...formData, firm: e.target.value})} 
                                style={inputStyle}
                            >
                                <option value="">Select Firm</option>
                                {firms.map(f => <option key={f.id} value={f.name}>{f.name}</option>)}
                            </select>
                        </div>
                        <div>
                            <label style={labelStyle}>Sales Person</label>
                            <select value={formData.salesPerson} onChange={e => setFormData({...formData, salesPerson: e.target.value})} style={inputStyle}>
                                <option value="">Select...</option>
                                {config.sales_team.map(s => <option key={s} value={s}>{s}</option>)}
                            </select>
                        </div>
                    </div>

                    <div style={{ position: 'relative', marginBottom: '15px' }} ref={customerSearchRef}>
                        <label style={labelStyle}>Customer Name *</label>
                        <input
                            value={customerSearchTerm}
                            onChange={e => handleCustomerSearch(e.target.value)}
                            onFocus={() => customerSearchTerm && setShowCustSuggestions(true)}
                            style={{ ...inputStyle, marginBottom: 0 }}
                            placeholder="Search or type name..."
                            autoComplete="off"
                        />
                        {showCustSuggestions && (
                            <div style={dropdownStyle}>
                                {filteredCustomers.map(cust => (
                                    <div key={cust.id} onClick={() => selectCustomer(cust)} style={dropdownItemStyle} className="hover-highlight">
                                        {cust.display_name}
                                    </div>
                                ))}
                                <div
                                    onClick={() => { setShowNewCustModal(true); setShowCustSuggestions(false); }}
                                    style={{ ...dropdownItemStyle, color: '#2563eb', fontWeight: 'bold', borderTop: '1px solid #e5e7eb' }}
                                >
                                    + Add New Customer
                                </div>
                            </div>
                        )}
                    </div>
                    

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                        <div>
                            <label style={labelStyle}>Invoice Number *</label>
                            <input value={formData.invoiceNo} onChange={e => setFormData({...formData, invoiceNo: e.target.value})} style={inputStyle} />
                        </div>
                        <div>
                            <label style={labelStyle}>Total Amount *</label>
                            <input type="number" value={formData.totalAmount} onChange={e => setFormData({...formData, totalAmount: e.target.value})} style={inputStyle} />
                        </div>
                    </div>


                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                        <div>
                            <label style={labelStyle}>Issue Date</label>
                            <input type="date" value={formData.issueDate} onChange={e => setFormData({...formData, issueDate: e.target.value})} style={inputStyle} />
                        </div>
                        <div>
                            <label style={labelStyle}>Due Date</label>
                            <input type="date" value={formData.dueDate} onChange={e => setFormData({...formData, dueDate: e.target.value})} style={inputStyle} />
                        </div>
                    </div>

                    <label style={labelStyle}>Remark</label>
                    <textarea value={formData.remark} onChange={e => setFormData({...formData, remark: e.target.value})} style={{ ...inputStyle, minHeight: '60px' }} />

                    <button onClick={handleSave} style={btnPrimaryStyle}>Save Invoice</button>
                </div>
            </div>

            <NewCustomerModal
                show={showNewCustModal}
                onClose={() => setShowNewCustModal(false)}
                onSave={(cust) => {
                    setAllCustomers([...allCustomers, cust]);
                    selectCustomer(cust);
                    setShowNewCustModal(false);
                }}
                db={db}
                isMobile={false}
            />
        </>
    );
};

export const PaymentModal = ({ show, onClose, onSave, db, invoice }) => {
    const [amount, setAmount] = useState('');
    const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
    const [method, setMethod] = useState('Bank Transfer');
    const [remark, setRemark] = useState('');

    const handleRequestClose = () => {
        onClose();
    };

    const modalRef = useClickOutside(() => {
        if (show) handleRequestClose();
    });

    useEffect(() => {
        if (show) {
            setAmount('');
            setDate(new Date().toISOString().split('T')[0]);
            setMethod('Bank Transfer');
            setRemark('');
        }
    }, [show]);

    if (!show || !invoice) return null;

    const handleRecord = async () => {
        const payAmount = parseFloat(amount);
        if (!payAmount || payAmount <= 0) return alert("Enter valid amount");
        if (payAmount > invoice.balance) return alert("Amount exceeds remaining balance");

        try {
            const newPaid = (invoice.amountPaid || 0) + payAmount;
            const newBalance = invoice.totalAmount - newPaid;
            let newStatus = 'pending';
            if (newBalance <= 0) newStatus = 'paid';
            else if (newPaid > 0) newStatus = 'partial';

            // Add a corresponding revenue transaction
            const transactionPayload = {
                date: date,
                type: 'revenue',
                category: 'Invoice Payment', // Default category for invoice payments
                entityName: invoice.customerName, // Link to customer name for consistency
                amount: payAmount,
                remark: `Payment for Invoice ${invoice.invoiceNo} from ${invoice.customerName}`,
                createdAt: new Date().toISOString(),
                sourceType: 'invoice', // New: Link transaction to invoice
                sourceId: invoice.id,  // New: Store invoice ID
                invoiceNo: invoice.invoiceNo, // New: Store invoice number
                firm: invoice.firm // Ensure firm is stored in transaction
            };
            const newTransactionRef = await addDoc(collection(db, "accounts_transactions"), transactionPayload);
            const transactionId = newTransactionRef.id;

            const paymentObj = {
                amount: payAmount,
                date,
                method,
                remark,
                recordedAt: new Date().toISOString(),
                transactionId: transactionId // Store the transaction ID
            };

            await updateDoc(doc(db, "accounts_invoices", invoice.id), {
                amountPaid: newPaid,
                balance: newBalance,
                status: newStatus,
                payments: arrayUnion(paymentObj)
            });

            // Ensure 'Invoice Payment' category exists in accounts_categories
            const categoriesRef = collection(db, "accounts_categories");
            const q = query(categoriesRef, where("name", "==", "Invoice Payment"));
            const querySnapshot = await getDocs(q);
            if (querySnapshot.empty) {
                await addDoc(categoriesRef, { name: "Invoice Payment" });
            }

            onSave();
            onClose();
        } catch (e) {
            alert("Error recording payment");
        }
    };

    return (
        <div style={modalOverlayStyle}>
            <div style={modalContentStyle} ref={modalRef}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                    <h3 style={{ margin: 0 }}>Record Payment</h3>
                    <button onClick={handleRequestClose} style={{ background: 'none', border: 'none', cursor: 'pointer' }}><X /></button>
                </div>

                <div style={{ background: '#f8fafc', padding: '15px', borderRadius: '8px', marginBottom: '20px' }}>
                    <div style={{ fontSize: '12px', color: '#64748b' }}>Invoice: {invoice.invoiceNo}</div>
                    <div style={{ fontSize: '18px', fontWeight: 'bold' }}>Pending: ₹{invoice.balance?.toLocaleString('en-IN')}</div>
                </div>

                <label style={labelStyle}>Amount Received</label>
                <input type="number" value={amount} onChange={e => setAmount(e.target.value)} placeholder="0.00" style={inputStyle} />

                <label style={labelStyle}>Payment Date</label>
                <input type="date" value={date} onChange={e => setDate(e.target.value)} style={inputStyle} />

                <label style={labelStyle}>Payment Method</label>
                <select value={method} onChange={e => setMethod(e.target.value)} style={inputStyle}>
                    <option>Bank Transfer</option>
                    <option>UPI / GPay</option>
                    <option>Cash</option>
                    <option>Cheque</option>
                </select>

                <label style={labelStyle}>Notes</label>
                <textarea value={remark} onChange={e => setRemark(e.target.value)} style={{ ...inputStyle, minHeight: '60px' }} />

                <button onClick={handleRecord} style={btnPrimaryStyle}>Confirm Payment</button>
            </div>
        </div>
    );
};

export const InvoicePaymentsDetailModal = ({ show, onClose, invoice, db }) => {
    const modalRef = useClickOutside(() => {
        if (show) onClose();
    });

    if (!show || !invoice) return null;

    return (
        <div style={modalOverlayStyle}>
            <div style={{ ...modalContentStyle, width: '600px', overflow: 'visible' }} ref={modalRef}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                    <h3 style={{ margin: 0 }}>Payments for Invoice {invoice.invoiceNo}</h3>
                    <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer' }}><X /></button>
                </div>

                <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                    {invoice.payments && invoice.payments.length > 0 ? (
                        invoice.payments.map((payment, index) => (
                            <div key={index} style={{ borderBottom: '1px solid #f1f5f9', padding: '10px 0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <div>
                                    <div style={{ fontWeight: 'bold' }}>₹{payment.amount.toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}</div>
                                    <div style={{ fontSize: '12px', color: '#64748b' }}>{new Date(payment.date).toLocaleDateString()} - {payment.method}</div>
                                    {payment.remark && <div style={{ fontSize: '11px', color: '#94a3b8', marginTop: '4px' }}>{payment.remark}</div>}
                                </div>
                                {payment.transactionId && (
                                    <Link
                                        to={`/accounts/transactions?highlight=${payment.transactionId}`}
                                        style={{ fontSize: '12px', color: '#2563eb', textDecoration: 'none', padding: '6px 10px', border: '1px solid #e2e8f0', borderRadius: '6px' }}
                                        onClick={onClose} // Close modal when navigating
                                    >
                                        View Transaction
                                    </Link>
                                )}
                            </div>
                        ))
                    ) : (
                        <p style={{ textAlign: 'center', color: '#64748b' }}>No payments recorded for this invoice.</p>
                    )}
                </div>
            </div>
        </div>
    );
};